import { useState } from 'react';

function Spinner() {
  return (
    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg className="w-10 h-10 text-[#ebebeb]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
    </svg>
  );
}

function HypothesisCard({ hypothesis, index }) {
  return (
    <div
      className="bg-white rounded-lg overflow-hidden"
      style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px, rgba(0,0,0,0.04) 0px 2px 2px' }}
    >
      <div className="bg-[#171717] px-4 py-3">
        <div className="flex items-start gap-3">
          <span className="flex-shrink-0 text-white/50 text-xs font-semibold mt-0.5 w-5">{index}.</span>
          <p className="text-white font-medium text-sm leading-snug">
            {hypothesis.hypothesis || hypothesis.statement || 'No hypothesis text'}
          </p>
        </div>
      </div>

      <div className="px-4 py-3" style={{ borderBottom: '1px solid #ebebeb' }}>
        <p className="text-xs font-semibold text-[#808080] uppercase tracking-wide mb-1">Rationale</p>
        <p className="text-sm text-[#4d4d4d] leading-relaxed">
          {hypothesis.rationale || 'No rationale provided.'}
        </p>
      </div>

      <div className="px-4 py-3" style={{ borderBottom: '1px solid #ebebeb' }}>
        <p className="text-xs font-semibold text-[#808080] uppercase tracking-wide mb-1">Experimental Approach</p>
        <p className="text-sm text-[#4d4d4d] leading-relaxed">
          {hypothesis.experimental_approach || hypothesis.approach || 'No experimental approach provided.'}
        </p>
      </div>

      <div className="px-4 py-3">
        <p className="text-xs font-semibold text-[#808080] uppercase tracking-wide mb-1">Expected Outcome</p>
        <p className="text-sm text-[#4d4d4d] leading-relaxed">
          {hypothesis.expected_outcome || hypothesis.outcome || 'No expected outcome provided.'}
        </p>
      </div>
    </div>
  );
}

export default function HypothesisPanel({ papers = [], isPro = true, apiCall }) {
  const [subject, setSubject] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const canSubmit = subject.trim() && papers.length >= 1 && !loading && isPro;

  const handleGenerate = async () => {
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const response = await apiCall(subject.trim());
      setResult(response.data || response);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate hypotheses. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isPro) {
    return (
      <div className="space-y-4">
        <div>
          <h2 className="text-xl font-semibold text-[#171717]" style={{ letterSpacing: '-1.28px' }}>Hypothesis Generator</h2>
          <p className="text-sm text-[#4d4d4d] mt-1">Generate novel research hypotheses grounded in your workspace papers.</p>
        </div>
        <div
          className="bg-white rounded-lg p-8 flex flex-col items-center text-center gap-4"
          style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
        >
          <LockIcon />
          <div>
            <p className="text-[#171717] font-semibold text-sm">Pro Feature</p>
            <p className="text-[#808080] text-sm mt-1 max-w-sm">
              Hypothesis generation is available on Pro and above.
            </p>
          </div>
          <a href="/pricing" className="px-5 py-2 bg-[#171717] text-white text-sm font-medium rounded-md hover:bg-[#2e2e2e] transition-colors">
            Upgrade to Pro
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-[#171717]" style={{ letterSpacing: '-1.28px' }}>Hypothesis Generator</h2>
        <p className="text-sm text-[#4d4d4d] mt-1">Generate novel, testable research hypotheses grounded in your workspace papers.</p>
      </div>

      <div className="bg-white rounded-lg p-5 space-y-4" style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}>
        <div>
          <label className="block text-sm font-medium text-[#171717] mb-1" style={{ letterSpacing: '-0.32px' }}>
            Research Subject / Question
          </label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && canSubmit && handleGenerate()}
            placeholder="e.g. CRISPR base editing in T-cells"
            className="w-full rounded-md px-3 py-2 text-sm text-[#171717] placeholder-[#808080] bg-white focus:outline-none focus:ring-2 focus:ring-[#0a72ef]"
            style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
            disabled={loading}
          />
        </div>

        {papers.length === 0 && (
          <p className="text-xs text-amber-700 bg-amber-50 rounded px-3 py-2" style={{ boxShadow: 'rgba(251,191,36,0.3) 0px 0px 0px 1px' }}>
            Add papers to your workspace first to generate contextual hypotheses.
          </p>
        )}

        <button
          onClick={handleGenerate}
          disabled={!canSubmit}
          className="w-full px-4 py-2 bg-[#171717] text-white rounded-md font-medium text-sm
                     hover:bg-[#2e2e2e] disabled:opacity-50 disabled:cursor-not-allowed
                     transition-colors flex items-center justify-center gap-2"
        >
          {loading ? (
            <><Spinner />Generating hypotheses...</>
          ) : (
            <>Generate Hypotheses ({papers.length} paper{papers.length !== 1 ? 's' : ''})</>
          )}
        </button>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 rounded px-3 py-2">{error}</p>
        )}
      </div>

      {result && (
        <div className="space-y-4">
          {result.mock_mode && (
            <div className="bg-amber-50 rounded-md px-4 py-2 text-xs text-amber-700 font-medium" style={{ boxShadow: 'rgba(251,191,36,0.3) 0px 0px 0px 1px' }}>
              MOCK MODE — Add your OPENROUTER_API_KEY to backend/.env for real AI hypotheses
            </div>
          )}
          <h3 className="text-sm font-medium text-[#4d4d4d]">
            Hypotheses for: <span className="text-[#171717] font-semibold">{result.subject || subject}</span>
          </h3>
          <div className="space-y-4">
            {(result.hypotheses || []).map((hypothesis, idx) => (
              <HypothesisCard key={idx} hypothesis={hypothesis} index={idx + 1} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
